import { IconArrowRight } from '@consta/icons/IconArrowRight'
import { Breadcrumbs } from '@consta/uikit/Breadcrumbs'
import { User } from '@consta/uikit/User'
import { withTooltip } from '@consta/uikit/withTooltip'
import type { MouseEventHandler, ReactNode } from 'react'
import { forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useBooleanFlag, useMetaChange } from '../../../hooks'
import { getToken, parseToken } from '../../../token'
import { Logo } from '../../Logo'
import { ThemeSwitch } from '../../Theme'
import { Header } from '../Header'
import { NavigationSidebar } from '../NavigationSidebar'
import css from './ProcessHeader.css'

type BreadCrumbs = {
  label: string
  href?: string
  onClick?: MouseEventHandler
}[]

type ProcessHeaderProps = {
  projectId: string
  projectName: string
  processName: string
  leftSlot?: ReactNode
  rightSlot?: ReactNode
  additionalCrumbs?: BreadCrumbs
}

const UserWithTooltip = withTooltip({
  mode: 'mouseover',
  appearTimeout: 500,
  direction: 'downCenter',
})(User)

export const ProcessHeader = forwardRef<HTMLElement, ProcessHeaderProps>(function ProcessHeader(
  { processName, projectName, projectId, leftSlot, rightSlot, additionalCrumbs = [] },
  ref,
) {
  const [isSidebarOpen, sidebarApi] = useBooleanFlag(false)

  const navigate = useNavigate()
  const userInfo = parseToken(getToken() ?? '')
  const projectUrl = `/project/${projectId}`

  const displayName = userInfo.preferred_username.split('.').join('')
  const userName = userInfo.name

  const breadCrumbs: BreadCrumbs = [
    {
      label: projectName,
      onClick: e => {
        // Магия: preventDefault не позволяет при клике перезагружать страницу
        // но позволяет через href открывать страницу в новой вкладке
        e.preventDefault()
        navigate(projectUrl)
      },
      href: projectUrl,
    },
    {
      label: processName,
      onClick: sidebarApi.setTrue,
    },
    ...additionalCrumbs,
  ]

  useMetaChange(`${processName} | ${projectName}`)

  return (
    <>
      <Header ref={ref} className={css.header}>
        <Header.LeftBlock>
          <Logo />
          <IconArrowRight size="xs" view="ghost" />
          <Breadcrumbs
            className={css.breadcrumbs}
            size="s"
            items={breadCrumbs}
            lastItemIsLink={true}
            fitMode="scroll"
          />
          {leftSlot}
        </Header.LeftBlock>
        <Header.RightBlock>
          {rightSlot}
          <ThemeSwitch />
          <UserWithTooltip
            status="available"
            name={userName}
            onlyAvatar={true}
            tooltipProps={{ content: displayName }}
          />
        </Header.RightBlock>
      </Header>
      <NavigationSidebar
        projectId={projectId}
        projectName={projectName}
        isOpen={isSidebarOpen}
        handleClose={sidebarApi.setFalse}
      />
    </>
  )
})
